//Frontend/src/unicodex_ui/pages/PurchaserDashboardPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ManufacturerNav from "../components/ManufacturerNav";
import portalPurchasingApi from "../services/portalPurchasingApi";
import { clearPortalSession, getStoredPermissions, getStoredPortalUser } from "../services/authApi";
import "../styles/PortalBusiness.css";

function PurchaserDashboardPage() {
  const navigate = useNavigate();
  const perms = getStoredPermissions();
  const user = getStoredPortalUser();

  const links = useMemo(
    () => [
      { to: "/portal/purchasing", label: "Purchasing" },
      { to: "/portal/inventory", label: "Inventory", perm: "inventory.stock.levels.read" },
      { to: "/portal/ops", label: "Ops", perm: "purchasing.po.read" }, // org_admin will still be route-guarded
    ],
    []
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [goodsReceipts, setGoodsReceipts] = useState([]);
  const [branches, setBranches] = useState([]);

  const canCreatePO = perms.includes("purchasing.po.create");
  const canApprovePO = perms.includes("purchasing.po.approve");
  const canCreateGR = perms.includes("purchasing.gr.create");
  const canPostGR = perms.includes("purchasing.gr.post");

  // Minimal PO form
  const [poBranchId, setPoBranchId] = useState("");
  const [poSupplierId, setPoSupplierId] = useState("");
  const [poOrderDate, setPoOrderDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [poCurrency, setPoCurrency] = useState("USD");
  const [poNotes, setPoNotes] = useState("");
  const [poItems, setPoItems] = useState([{ productId: "", quantity: 1, unitCost: 0 }]);

  function signOut() {
    clearPortalSession();
    navigate("/portal/login", { replace: true });
  }

  async function load() {
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      const [bRes, sRes, poRes, grRes] = await Promise.all([
        portalPurchasingApi.listBranches(),
        portalPurchasingApi.listSuppliers({ limit: 50, offset: 0 }),
        portalPurchasingApi.listPurchaseOrders({ limit: 20, offset: 0 }),
        portalPurchasingApi.listGoodsReceipts({ limit: 20, offset: 0 }),
      ]);

      if (bRes?.ok !== true) throw new Error("branches_failed");
      if (sRes?.ok !== true) throw new Error("suppliers_failed");
      if (poRes?.ok !== true) throw new Error("po_failed");
      if (grRes?.ok !== true) throw new Error("gr_failed");

      const branchRows = Array.isArray(bRes.rows) ? bRes.rows : Array.isArray(bRes.branches) ? bRes.branches : [];
      setBranches(branchRows);
      // Default branch for PO creation
      if (!poBranchId && branchRows.length) setPoBranchId(String(branchRows[0].id));

      // Backend returns list payloads as { rows, total, limit, offset }
      setSuppliers(Array.isArray(sRes.rows) ? sRes.rows : []);
      setPurchaseOrders(Array.isArray(poRes.rows) ? poRes.rows : []);
      setGoodsReceipts(Array.isArray(grRes.rows) ? grRes.rows : []);
    } catch (e) {
      console.error("[PurchaserDashboard] load error:", e);
      setError("Unable to load purchasing data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updatePoItem(idx, patch) {
    setPoItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function addPoItem() {
    setPoItems((arr) => [...arr, { productId: "", quantity: 1, unitCost: 0 }]);
  }

  function removePoItem(idx) {
    setPoItems((arr) => arr.filter((_, i) => i !== idx));
  }

  function money(n, currency = "") {
    const v = Number(n || 0);
    if (!Number.isFinite(v)) return "-";
    return currency ? `${currency} ${v.toFixed(2)}` : v.toFixed(2);
  }

  function calcPoTotal() {
    return poItems.reduce((sum, it) => {
      const qty = Number(it.quantity || 0);
      const cost = Number(it.unitCost || 0);
      return sum + (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(cost) ? cost : 0);
    }, 0);
  }

  async function createPO(e) {
    e.preventDefault();
    setError("");
    setOkMsg("");

    const cleanItems = poItems
      .map((it) => ({
        productId: Number(it.productId),
        quantityOrdered: Number(it.quantity),
        unitCost: Number(it.unitCost),
      }))
      .filter((it) => Number.isFinite(it.productId) && it.productId > 0 && it.quantityOrdered > 0);

    if (!poBranchId || !poSupplierId || !cleanItems.length) {
      setError("Branch, supplier and at least one valid item line are required.");
      return;
    }

    try {
      const payload = {
        branchId: Number(poBranchId),
        supplierId: Number(poSupplierId),
        orderDate: poOrderDate,
        notes: poNotes.trim() || undefined,
        currency: poCurrency,
        items: cleanItems,
      };

      const res = await portalPurchasingApi.createPurchaseOrder(payload);
      if (res?.ok !== true) throw new Error("create_po_failed");

      setOkMsg("Purchase order created.");
      setPoNotes("");
      setPoItems([{ productId: "", quantity: 1, unitCost: 0 }]);

      await load();
    } catch (e) {
      console.error("[PurchaserDashboard] create PO error:", e);
      setError("Unable to create PO. Check required fields and item lines.");
    }
  }

  async function approvePO(id) {
    setError("");
    setOkMsg("");
    try {
      const res = await portalPurchasingApi.approvePurchaseOrder(id);
      if (res?.ok !== true) throw new Error("approve_failed");
      setOkMsg("PO approved.");
      await load();
    } catch (e) {
      console.error("[PurchaserDashboard] approve error:", e);
      setError("Unable to approve PO.");
    }
  }

  async function markSentPO(id) {
    setError("");
    setOkMsg("");
    try {
      const res = await portalPurchasingApi.markSentPurchaseOrder(id);
      if (res?.ok !== true) throw new Error("mark_sent_failed");
      setOkMsg("PO marked sent.");
      await load();
    } catch (e) {
      console.error("[PurchaserDashboard] mark sent error:", e);
      setError("Unable to mark PO as sent.");
    }
  }

  async function cancelPO(id) {
    setError("");
    setOkMsg("");
    try {
      const res = await portalPurchasingApi.cancelPurchaseOrder(id);
      if (res?.ok !== true) throw new Error("cancel_failed");
      setOkMsg("PO cancelled.");
      await load();
    } catch (e) {
      console.error("[PurchaserDashboard] cancel error:", e);
      setError("Unable to cancel PO.");
    }
  }

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav
          title="Business Portal"
          subtitle={user?.email || "Signed in"}
          links={links}
          onSignOut={signOut}
        />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">Purchasing</h1>
              <p className="ucx-portal-sub">
                Suppliers, purchase orders, and goods receipts (based on your permissions).
              </p>
            </div>
            <div className="ucx-portal-actions">
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={load} disabled={loading}>
                Refresh
              </button>
              {perms.includes("inventory.stock.levels.read") && (
                <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => navigate("/portal/inventory")}>
                  Inventory
                </button>
              )}
            </div>
          </div>

          {error && <div className="ucx-portal-error">{error}</div>}
          {okMsg && <div className="ucx-portal-success">{okMsg}</div>}

          {loading ? (
            <div className="ucx-muted">Loading…</div>
          ) : (
            <>
              <div className="ucx-portal-grid">
                <div className="ucx-portal-card">
                  <div className="ucx-portal-card-label">Suppliers</div>
                  <div className="ucx-portal-card-value">{suppliers.length}</div>
                </div>
                <div className="ucx-portal-card">
                  <div className="ucx-portal-card-label">Purchase Orders</div>
                  <div className="ucx-portal-card-value">{purchaseOrders.length}</div>
                </div>
                <div className="ucx-portal-card">
                  <div className="ucx-portal-card-label">Goods Receipts</div>
                  <div className="ucx-portal-card-value">{goodsReceipts.length}</div>
                </div>
              </div>

              {/* Supplier creation is admin/org-admin only; purchasers should not see it */}
              {canCreatePO && (
                <>
                  <div style={{ marginTop: 18 }} className="ucx-portal-panel-title">
                    Create Purchase Order
                  </div>

                  <form onSubmit={createPO}>
                    <div className="ucx-portal-form">
                      <div className="ucx-portal-field">
                        <label>Branch *</label>
                        <select value={poBranchId} onChange={(e) => setPoBranchId(e.target.value)} required>
                          <option value="">Select…</option>
                          {branches.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name || b.code || `Branch ${b.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="ucx-portal-field">
                        <label>Supplier *</label>
                        <select value={poSupplierId} onChange={(e) => setPoSupplierId(e.target.value)} required>
                          <option value="">Select…</option>
                          {suppliers.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="ucx-portal-field">
                        <label>Order date *</label>
                        <input type="date" value={poOrderDate} onChange={(e) => setPoOrderDate(e.target.value)} required />
                      </div>
                      <div className="ucx-portal-field">
                        <label>Currency</label>
                        <select value={poCurrency} onChange={(e) => setPoCurrency(e.target.value)}>
                          <option value="USD">USD</option>
                          <option value="BWP">BWP</option>
                          <option value="ZAR">ZAR</option>
                        </select>
                      </div>

                      <div className="ucx-portal-field" style={{ gridColumn: "1 / -1" }}>
                        <label>Notes</label>
                        <textarea value={poNotes} onChange={(e) => setPoNotes(e.target.value)} />
                      </div>
                    </div>

                    <div className="ucx-portal-lineitems">
                      <div className="ucx-portal-lineitems-head">
                        <div className="ucx-portal-panel-title" style={{ marginBottom: 0 }}>Items</div>
                        <button type="button" className="ucx-btn ucx-btn-outline" onClick={addPoItem}>
                          + Add item
                        </button>
                      </div>

                      <div className="ucx-portal-tablewrap">
                        <table className="ucx-portal-table">
                        <thead>
                          <tr>
                            <th style={{ width: 140 }}>Product ID</th>
                            <th style={{ width: 140 }}>Qty</th>
                            <th style={{ width: 180 }}>Unit cost</th>
                            <th>Line total</th>
                            <th style={{ width: 110 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {poItems.map((it, idx) => {
                            const qty = Number(it.quantity || 0);
                            const cost = Number(it.unitCost || 0);
                            const lineTotal = (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(cost) ? cost : 0);

                            return (
                              <tr key={idx}>
                                <td>
                                  <input
                                    className="ucx-portal-input-compact"
                                    value={it.productId}
                                    onChange={(e) => updatePoItem(idx, { productId: e.target.value.replace(/[^\d]/g, "") })}
                                    placeholder="e.g. 12"
                                  />
                                </td>
                                <td>
                                  <input
                                    className="ucx-portal-input-compact"
                                    type="number"
                                    min="1"
                                    value={it.quantity}
                                    onChange={(e) => updatePoItem(idx, { quantity: Number(e.target.value) })}
                                  />
                                </td>
                                <td>
                                  <input
                                    className="ucx-portal-input-compact"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={it.unitCost}
                                    onChange={(e) => updatePoItem(idx, { unitCost: Number(e.target.value) })}
                                  />
                                </td>
                                <td>{money(lineTotal, poCurrency)}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="ucx-btn ucx-btn-outline"
                                    onClick={() => removePoItem(idx)}
                                    disabled={poItems.length === 1}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>

                      <div className="ucx-portal-toolbar">
                        <div className="ucx-portal-toolbar-hint">
                          Estimated total: <b>{money(calcPoTotal(), poCurrency)}</b>
                        </div>
                        <div className="ucx-portal-actions" style={{ margin: 0 }}>
                          <button type="submit" className="ucx-btn ucx-btn-primary" disabled={!poSupplierId || !poBranchId}>
                            Create PO
                          </button>
                        </div>
                      </div>
                    </div>

                  </form>
                </>
              )}

              <h3 className="ucx-portal-title" style={{ marginTop: 18 }}>Recent Purchase Orders</h3>
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>PO No</th>
                      <th>Status</th>
                      <th>Supplier</th>
                      <th>Order date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrders.map((po) => (
                      <tr key={po.id}>
                        <td>{po.id}</td>
                        <td>{po.po_number || "—"}</td>
                        <td>{po.status || "—"}</td>
                        <td>{po.supplier_name || po.supplierId || "—"}</td>
                        <td>{po.order_date ? String(po.order_date).slice(0, 10) : "—"}</td>
                        <td>
                          <div className="ucx-portal-row-actions">
                            {canApprovePO && po.status === "draft" && (
                              <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => approvePO(po.id)}>
                                Approve
                              </button>
                            )}
                            {canApprovePO && po.status === "approved" && (
                              <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => markSentPO(po.id)}>
                                Mark sent
                              </button>
                            )}
                            {canApprovePO && ["draft", "approved"].includes(po.status) && (
                              <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => cancelPO(po.id)}>
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!purchaseOrders.length && (
                      <tr><td colSpan="6" className="ucx-muted">No purchase orders yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="ucx-portal-title" style={{ marginTop: 18 }}>Recent Goods Receipts</h3>
              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>GR No</th>
                      <th>Status</th>
                      <th>PO</th>
                      <th>Received date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goodsReceipts.map((gr) => (
                      <tr key={gr.id}>
                        <td>{gr.id}</td>
                        <td>{gr.gr_number || "—"}</td>
                        <td>{gr.status || "—"}</td>
                        <td>{gr.purchase_order_id || "—"}</td>
                        <td>{gr.received_date ? String(gr.received_date).slice(0, 10) : "—"}</td>
                        <td>
                          <div className="ucx-portal-row-actions">
                            {canPostGR && gr.status === "draft" && (
                              <button className="ucx-btn ucx-btn-outline" type="button" onClick={() => portalPurchasingApi.postGoodsReceipt(gr.id).then(load).catch(() => setError("Unable to post GR."))}>
                                Post
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!goodsReceipts.length && (
                      <tr><td colSpan="6" className="ucx-muted">No goods receipts yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {canCreateGR && (
                <div className="ucx-muted" style={{ marginTop: 10 }}>
                  Goods receipt creation UI can be added next (uses the same endpoints already wired).
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default PurchaserDashboardPage;
